import React, { useCallback, useState } from 'react';
import { ArrowLeft, FlaskConical, ArrowRight } from 'lucide-react';
import type { AppView, UserProfile } from '../../types';
import type { TutorialKapitelId } from '../../tutorial/types';
import { kapitelNachId } from '../../tutorial/register';
import { STOLPERFALLEN, stolperfallenFuer } from '../../tutorial/stolperfallenLoader';
import { useLernzentrum } from '../../hooks/useLernzentrum';
import { LernzentrumStart } from './LernzentrumStart';
import { KapitelSeite } from './KapitelSeite';
import { Uebungsplatz } from './Uebungsplatz';

/**
 * LERNZENTRUM — der Container (AppView `lernzentrum`).
 *
 * Duenn nach Hausregel: er laedt ueber `useLernzentrum`, haelt genau einen
 * Zustand (welche der drei Seiten gerade offen ist) und reicht alles andere
 * weiter. Die Rechnungen stehen in `tutorialLogic.ts`, die Darstellung in
 * `LernzentrumStart` und `KapitelSeite`.
 *
 * Drei Seiten, kein Router — wie im Rest der App:
 *   start   — Lernpfad, Suche, Bereichskacheln
 *   kapitel — eine Anleitung
 *   uebung  — der Übungsplatz (Auftrag A4)
 */

interface Props {
    userProfile: UserProfile | null;
    /**
     * „Jetzt in der App zeigen". Das Tour-Overlay kommt in Paket B; bis dahin
     * wechselt die App in die View des Kapitels und merkt sich die Kapitel-ID
     * unter `sessionStorage['lernzentrum.tour']`. Das Overlay liest sie
     * spaeter genau dort — der Weg dorthin muss dann nicht noch einmal gebaut
     * werden.
     */
    onStarteTour: (kapitelId: TutorialKapitelId, view: AppView) => void;
    /** Sprung in ein Übungsprojekt (Auftrag A4 liefert Id und Zielansicht). */
    onOeffneProjekt: (projectId: string, view: AppView) => void;
    /**
     * Direkt auf ein Kapitel springen — so kommt das Kontext-„?" hierher.
     * Nur der ANFANGSwert: danach steuert die Ansicht sich selbst, sonst
     * spraenge sie bei jedem Zurück wieder auf dasselbe Kapitel.
     */
    initialKapitelId?: TutorialKapitelId;
    /**
     * „Dazu fragen" auf einer Kapitelseite — oeffnet `HilfeChat` mit diesem
     * Kapitel im Ruecken (Plan §1.8, Paket E). Fehlt der Rueckkanal, reicht
     * `KapitelSeite` das durch: der Knopf bleibt sichtbar und abgeschaltet.
     */
    onDazuFragen?: (kapitelId: TutorialKapitelId) => void;
}

const ZurueckKnopf: React.FC<{ onClick: () => void; label: string }> = ({ onClick, label }) => (
    <button
        type="button"
        onClick={onClick}
        className="flex items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors text-xs font-bold mb-4"
    >
        <ArrowLeft className="w-3.5 h-3.5" />
        <span className="uppercase tracking-wider text-[11px]">{label}</span>
    </button>
);

type Ansicht = { typ: 'start' } | { typ: 'kapitel'; id: TutorialKapitelId } | { typ: 'uebung' };

export const LernzentrumView: React.FC<Props> = ({ userProfile, onStarteTour, onOeffneProjekt, initialKapitelId, onDazuFragen }) => {
    const { kapitel, pfad, inhalte, fortschritt, laedt, merkeGesehen, merkeAbgeschlossen } = useLernzentrum(userProfile);
    const [ansicht, setAnsicht] = useState<Ansicht>(
        () => (initialKapitelId && kapitelNachId(initialKapitelId) ? { typ: 'kapitel', id: initialKapitelId } : { typ: 'start' }),
    );

    const oeffneKapitel = useCallback((id: TutorialKapitelId) => {
        setAnsicht({ typ: 'kapitel', id });
        merkeGesehen(id);
    }, [merkeGesehen]);

    const zumStart = useCallback(() => setAnsicht({ typ: 'start' }), []);
    const zurUebung = useCallback(() => setAnsicht({ typ: 'uebung' }), []);

    const starteTour = useCallback((id: TutorialKapitelId) => {
        const k = kapitelNachId(id);
        if (k) onStarteTour(id, k.view);
    }, [onStarteTour]);

    // ── Übungsplatz ──
    if (ansicht.typ === 'uebung') {
        return (
            <div data-glass-view="lernzentrum" className="h-full overflow-y-auto bg-slate-50">
                <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 pb-24">
                    <ZurueckKnopf onClick={zumStart} label="Lernzentrum" />
                    <Uebungsplatz onOeffneProjekt={onOeffneProjekt} />
                </div>
            </div>
        );
    }

    // ── Eine Kapitelseite ──
    //
    // Steht die ID nicht mehr im Register (umbenannt, aus v1 gefallen), faellt
    // die Ansicht still auf die Startseite zurueck statt eine leere Seite oder
    // eine Fehlermeldung zu zeigen: eine ungueltige ID ist ein Bauzustand,
    // kein Nutzerfehler.
    const offenesKapitel = ansicht.typ === 'kapitel' ? kapitelNachId(ansicht.id) : undefined;
    if (offenesKapitel) {
        return (
            <KapitelSeite
                kapitel={offenesKapitel}
                daten={inhalte.get(offenesKapitel.id)}
                stolperfallen={stolperfallenFuer(offenesKapitel.id)}
                abgeschlossen={!!fortschritt.get(offenesKapitel.id)?.abgeschlossenAm}
                onZurueck={zumStart}
                onStarteTour={starteTour}
                onSelbstAusprobieren={zurUebung}
                onDazuFragen={onDazuFragen}
                onAbschliessen={merkeAbgeschlossen}
            />
        );
    }

    // ── Startseite ──
    return (
        <LernzentrumStart
            kapitel={kapitel}
            pfad={pfad}
            fortschritt={fortschritt}
            stolperfallen={STOLPERFALLEN}
            laedt={laedt}
            onOeffneKapitel={oeffneKapitel}
            uebungsplatz={<UebungsplatzEinstieg onOeffnen={zurUebung} />}
        />
    );
};

/**
 * Einstiegskarte zum Übungsplatz am Fuss der Startseite.
 *
 * Bewusst eine Karte und kein zweites Kachelraster: der Übungsplatz bringt
 * seine eigene Kachelwand mit, und zwei Raster untereinander liessen die
 * Startseite wie zwei Seiten aussehen, die versehentlich zusammengeklebt sind.
 */
const UebungsplatzEinstieg: React.FC<{ onOeffnen: () => void }> = ({ onOeffnen }) => (
    <button
        type="button"
        onClick={onOeffnen}
        className="group w-full text-left bg-white p-5 rounded-2xl border border-slate-200 hover:border-emerald-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex items-center gap-4"
    >
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-100 w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
            <FlaskConical className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="min-w-0 flex-1">
            <h3 className="font-bold text-slate-800 text-sm">Übungsplatz</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
                Eigene Übungsprojekte anlegen und alles gefahrlos ausprobieren — nichts davon zählt in Abrechnung, Zähler oder Auswertung.
            </p>
        </div>
        <span className="shrink-0 flex items-center gap-1 text-emerald-600 text-[11px] font-bold">
            Öffnen<ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </span>
    </button>
);
